import { Component, OnInit, OnDestroy, computed, inject, signal, Input, HostBinding, ChangeDetectionStrategy, booleanAttribute } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { RouterModule } from '@angular/router';
import { ApiUserService } from '../../shared/services/api-user/api-user.service';

import { BehaviorSubject, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { AuthenticationService } from '../../shared/services/authentication/authentication.service';
import { UserWithDetails } from '@app/shared/interfaces';
import { AvatarHelper } from '../../shared/helpers/avatar.helper';

// Define local interfaces for account management
interface UserAccount {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    initials: string;
  };
  isActive: boolean;
}

interface UserProfile {
  id: string;
  userId?: string | number;
  email: string;
  name?: string;
  picture?: string;
  pictureUrl?: string | null;
  roles?: string[];
  permissions?: string[];
  metadata?: Record<string, unknown>;
}

// Define avatar colors
@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatSlideToggleModule,
    RouterModule
  ],
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfileComponent implements OnInit, OnDestroy {
  /** When true, removes sidebar-specific spacing/border so the component
   *  renders cleanly inside a header bar. */
  @Input({ transform: booleanAttribute }) compact = false;

  @HostBinding('class.compact') get compactClass() { return this.compact; }

  private userService = inject(ApiUserService);
  private readonly router = inject(Router);
  private authService = inject(AuthenticationService);

  currentAccount = signal<(UserAccount & { color: string }) | null>(null);
  accounts: (UserAccount & { color: string })[] = [];

  // Create a BehaviorSubject to simulate the currentAccount$ observable
  private currentAccountSubject = new BehaviorSubject<UserAccount | null>(null);
  currentAccount$ = this.currentAccountSubject.asObservable();

  // User profile from auth service
  userProfile = signal<UserProfile | null>(null);

  // Avatar data URL (uploaded blob, takes priority over OAuth picture)
  avatarUrl = signal<string | null>(null);

  /**
   * Background for the initials circle shown before any avatar image resolves.
   *
   * The fallback here was a hardcoded `#8444AA`, so every user with no account
   * loaded yet got the same purple — and it did not match the colour the same
   * user's avatar settled on a moment later. Keyed on the account, then the
   * profile id, then the email, so it is stable for a given person whichever of
   * those has arrived.
   */
  avatarPlaceholderColor = computed(() => {
    const account = this.currentAccount();
    if (account) {
      return account.color;
    }
    const profile = this.userProfile();
    return AvatarHelper.getAvatarColorForKey(profile?.id ?? profile?.email ?? '');
  });

  private subscriptions = new Subscription();

  constructor() {
    // Initialize with data from API
    this.initializeUserAccounts();
  }

  ngOnInit(): void {
    // Subscribe to the currentAccount$ observable
    this.subscriptions.add(
      this.currentAccount$.subscribe(account => {
        if (account) {
          this.currentAccount.set(this.addColorToAccount(account));
        } else {
          this.currentAccount.set(null);
        }
      })
    );
    
    // Subscribe to the user profile from auth service
    this.subscriptions.add(
      this.authService.userProfile$.subscribe(profile => {
        this.userProfile.set(profile);
        // When profile updates, reload accounts to reflect changes
        if (profile) {
          this.reloadUserAccounts();
        }
      })
    );

    // Subscribe to the avatar URL (uploaded blob takes priority over OAuth picture)
    this.subscriptions.add(
      this.authService.avatarUrl$.subscribe(url => {
        this.avatarUrl.set(url);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private initializeUserAccounts(): void {
    // Fetch the current user from the API
    this.userService.getCurrentUser().subscribe((response: UserWithDetails) => {
      // Convert the API response to a UserAccount object
      const userAccount: UserAccount = {
        id: response.userId?.toString() || '1',
        user: {
          id: response.userId?.toString() || '1',
          firstName: response.name?.split(' ')[0] || '',
          lastName: response.name?.split(' ')[1] || '',
          email: response.email || '',
          initials: this.getInitialsFromName(response.name || '')
        },
        isActive: true
      };
      
      // Set as current account
      this.currentAccountSubject.next(userAccount);
      const coloredAccount = this.addColorToAccount(userAccount);
      this.currentAccount.set(coloredAccount);

      // For demo purposes, we'll just use the current account
      // In a real app, you would fetch all user accounts from an API
      this.accounts = [coloredAccount];
    });
  }

  /**
   * Reload user accounts from the API
   * This can be called when the profile is updated elsewhere
   */
  private reloadUserAccounts(): void {
    this.initializeUserAccounts();
  }

  private getInitialsFromName(name: string): string {
    const parts = name
      .split(' ')
      .map(part => part.trim())
      .filter(part => part.length > 0);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    } else if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return 'U';
  }

  private addColorToAccount(account: UserAccount): UserAccount & { color: string } {
    // Was a local 20-entry flat-UI palette with its own hash. Several of those
    // entries (#ecf0f1, #bdc3c7) are near-white, and the initials drawn on top
    // are white — so an account could land on an avatar with no readable
    // initials at all. The shared palette is the design system's band.
    return { ...account, color: AvatarHelper.getAvatarColorForKey(account.id) };
  }

  switchAccount(accountId: string): void {
    // Find the account with the given ID
    const account = this.accounts.find(acc => acc.id === accountId);
    if (account) {
      // Update the current account
      this.currentAccountSubject.next(account);
      this.currentAccount.set(this.addColorToAccount(account));
      
      // In a real app, you would call an API to switch accounts
      // For now, we just update the UI
    }
  }

  logout(): void {
    // Use the auth service logout method
    this.authService.logout();
  }
}
